import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { authApi, type Session } from '../../api/auth';

export function ActiveSessionsPage() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      setSessions(await authApi.listSessions());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function revoke(id: string) {
    await authApi.revokeSession(id);
    await load();
  }

  async function revokeAll() {
    await authApi.revokeAllSessions();
    await load();
  }

  return (
    <div className="mx-auto max-w-3xl p-6">
      <h1 className="text-2xl font-semibold text-slate-900">Active sessions</h1>
      <p className="mt-1 text-sm text-slate-600">Devices currently signed in to your account.</p>
      {error && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      <div className="mt-6 flex gap-3">
        <button type="button" onClick={() => void load()} className="rounded-lg border border-slate-300 px-4 py-2 text-sm">
          Refresh
        </button>
        <button type="button" onClick={() => void revokeAll()} className="rounded-lg bg-red-600 px-4 py-2 text-sm text-white">
          Sign out all other sessions
        </button>
      </div>
      {loading ? (
        <p className="mt-6 text-slate-600">Loading…</p>
      ) : (
        <ul className="mt-6 space-y-3">
          {sessions.map((s) => (
            <li key={s.id} className="flex items-start justify-between rounded-xl border border-slate-200 bg-white p-4">
              <div>
                <p className="font-medium text-slate-900">{s.user_agent || 'Unknown device'}</p>
                <p className="text-sm text-slate-600">IP: {s.ip_address || '—'}</p>
                <p className="text-xs text-slate-500">Last active: {new Date(s.last_active).toLocaleString()}</p>
              </div>
              <button type="button" onClick={() => void revoke(s.id)} className="text-sm text-red-600 hover:underline">
                Revoke
              </button>
            </li>
          ))}
          {sessions.length === 0 && <li className="text-sm text-slate-600">No active sessions.</li>}
        </ul>
      )}
      <p className="mt-6 text-sm">
        <Link to="/account/security" className="text-blue-600 hover:underline">Security dashboard</Link>
        {' · '}
        <Link to="/account/profile" className="text-blue-600 hover:underline">Back to profile</Link>
      </p>
    </div>
  );
}
